import { lazy } from "react";
import { PageRoutes } from "../constants/constants";
import { IRoute } from "./RouteInterface";

export const routes: IRoute[] = [
  // Design System component specimen — dev/internal only (T011, US4).
  // Never registered in production builds.
  ...(import.meta.env.DEV
    ? [
        {
          component: lazy(() => import("../components/designSystem/Specimen").then((m) => ({ default: m.Specimen }))),
          path: "/design-system",
          exact: true,
        } as IRoute,
      ]
    : []),
  // create account process routes
  {
    component: lazy(() => import("../components/createAccount/register")),
    path: "/create-account",

    exact: true,
    container: lazy(() => import("../components/createAccount")),
  },
  {
    component: lazy(() => import("../components/createAccount/verifyEmail/EmailVerified")),
    path: ["/email-verified", "/investor/email-verified"],

    exact: true,
    container: lazy(() => import("../components/createAccount")),
  },
  {
    component: lazy(() => import("../components/createAccount/verifyEmail/InvalidToken")),
    path: ["/invalid-token", "/investor/invalid-token"],
    exact: true,

    container: lazy(() => import("../components/createAccount")),
  },
  {
    component: lazy(() => import("../components/createAccount/verifyEmail/VerifyEmail")),
    path: ["/verify-email/:invalid?", "/investor/verify-email/:invalid?"],

    private: true,
    exact: true,
    container: lazy(() => import("../components/createAccount")),
  },
  {
    component: lazy(() => import("../components/createAccount/addPaymentMethod")),
    path: "/add-payment/:companyId",

    private: true,
    exact: true,
    container: lazy(() => import("../components/createAccount")),
  },
  {
    component: lazy(() => import("../components/createAccount/authoriseMystake/AuthoriseMystakeFirstStep")),
    path: "/authorise-mystake/:companyId",

    private: true,
    exact: true,
    container: lazy(() => import("../components/createAccount")),
  },
  {
    component: lazy(() => import("../components/createAccount/verifyId/VerifyId")),
    exact: true,
    path: ["/verify-id", "/investor/verify-id", "/admin/verify-id", "/wallet/verify-id"],

    private: true,
    container: lazy(() => import("../components/createAccount")),
  },
  {
    component: lazy(() => import("../components/createAccount/authoriseMystake/AuthoriseMystakeFinalStep")),
    exact: true,
    path: "/authorise-mystake-final-step/:companyId",

    private: true,
    container: lazy(() => import("../components/createAccount")),
  },
  {
    component: lazy(() =>
      import("../components/createAccount/verifyEmail/InvitationRevoked").then((module) => ({
        default: module.InvitationRevoked,
      }))
    ),
    path: "/invitation-revoked",
    private: true,
    exact: true,
  },
  // director process routes
  {
    component: lazy(() => import("../components/directorFlow/Register")),
    exact: true,
    path: "/nominee/create-account",

    container: lazy(() => import("../components/directorFlow")),
  },
  {
    component: lazy(() => import("../components/createAccount/verifyEmail/EmailVerified")),
    exact: true,
    path: "/nominee/email-verified",

    container: lazy(() => import("../components/directorFlow")),
  },
  {
    component: lazy(() => import("../components/createAccount/verifyEmail/InvalidToken")),
    exact: true,
    path: "/nominee/invalid-token",

    container: lazy(() => import("../components/directorFlow")),
  },
  {
    component: lazy(() => import("../components/createAccount/verifyEmail/VerifyEmail")),
    exact: true,
    path: ["/nominee/verify-email", "/nominee/verify-email/:invalid?"],

    private: true,
    container: lazy(() => import("../components/directorFlow")),
  },
  {
    component: lazy(() => import("../components/createAccount/verifyId/VerifyId")),
    exact: true,
    path: "/nominee/verify-id",

    private: true,
    container: lazy(() => import("../components/directorFlow")),
  },
  {
    component: lazy(() => import("../components/directorFlow/AuthoriseMystakeFinalStep")),
    exact: true,
    path: "/nominee/authorise-mystake-final-step/:companyId",

    private: true,
    container: lazy(() => import("../components/directorFlow")),
  },
  {
    component: lazy(() => import("../components/pages/NomineeAccountCreated")),
    path: "/nominee-account-created",

    container: lazy(() => import("../components/pages")),
  },
  {
    component: lazy(() => import("../components/pages/AuthorisedAccountCreated")),
    path: [
      "/authorised-account-created",
      "/nominee/authorised-account-created",
      "/admin/authorised-account-created",
      "/investor/authorised-account-created",
    ],

    container: lazy(() => import("../components/pages")),
  },
  {
    component: lazy(() => import("../components/pages/AuthorisedAccountPending")),
    path: ["/authorised-account-pending", "/nominee/authorised-account-pending"],

    container: lazy(() => import("../components/pages")),
  },
  {
    component: lazy(() => import("../components/pages/LinkExpired")),
    path: "/link-expired",

    container: lazy(() => import("../components/pages")),
  },
  {
    component: lazy(() => import("../components/pages/PasswordResetLinkExpired")),
    path: "/password-reset-link-expired",

    container: lazy(() => import("../components/pages")),
  },
  {
    component: lazy(() => import("../components/pages/InactiveCompany")),
    path: "/inactive-company",
    exact: true,
    container: lazy(() => import("../components/pages")),
  },
  // admin routes
  {
    component: lazy(() => import("../components/createAdminAccount/Register")),
    path: "/admin/create-account",

    exact: true,
    container: lazy(() => import("../components/createAccount")),
  },
  // Signin routes
  {
    component: lazy(() => import("../components/signInFlow/signin")),
    path: "/signin",

    container: lazy(() => import("../components/signInFlow")),
  },
  {
    component: lazy(() => import("../components/pages/AvererInvite")),
    path: "/invite/averer",
    exact: true,
    container: lazy(() => import("../components/signInFlow")),
  },
  {
    component: lazy(() => import("../components/pages/OidcCallback")),
    path: "/oidc/callback",
    exact: true,
    container: lazy(() => import("../components/signInFlow")),
  },
  {
    component: lazy(() => import("../components/pages/SignedOut")),
    path: "/signed-out",
    exact: true,
    container: lazy(() => import("../components/signInFlow")),
  },
  {
    component: lazy(() => import("../components/signInFlow/forgotPassword")),
    path: "/forgot-password",

    container: lazy(() => import("../components/signInFlow")),
  },
  {
    component: lazy(() => import("../components/signInFlow/resetPassword")),
    path: "/reset-password/:token",

    container: lazy(() => import("../components/signInFlow")),
  },
  {
    component: lazy(() => import("../components/signInFlow/resetPassword/PasswordUpdated")),
    path: "/password-updated",
    exact: true,
    container: lazy(() => import("../components/signInFlow")),
  },

  // Company Routes
  {
    component: lazy(() => import("../components/companies/companyList")),
    path: PageRoutes.COMPANIES_LIST,
    private: true,
    exact: true,
  },
  {
    component: lazy(() => import("../components/companies/profileSetting")),
    path: "/account",
    private: true,
  },
  {
    component: lazy(() => import("../components/companies/switchCompany")),
    path: "/switch-company",
    private: true,
  },
  {
    component: lazy(() => import("../components/companies/addCompany")),
    path: ["/add-company", "/add-company/:companyId"],
    private: true,
    // container: lazy(() => import("../components/companies/addCompany")),
  },
  {
    component: lazy(() => import("../components/companies/admin-view")),
    path: ["/company/:companyId/admin-view", "/"],
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/investorRelations/announcements")),
    path: "/company/:companyId/investor-relations/announcements",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/investorRelations/announcements/Announcement")),
    path: [
      "/company/:companyId/investor-relations/announcement/:announcementId",
      "/investor/company/:companyId/investor-relations/investor/:shareholderId/announcement/:announcementId",
    ],
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/investorRelations/contacts")),
    path: "/company/:companyId/investor-relations/contacts",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/investorRelations/invitations")),
    path: "/company/:companyId/investor-relations/invitations",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() =>
      import("../components/companies/investors").then((module) => ({
        default: module.InvestorsPage,
      }))
    ),
    path: "/company/:companyId/investors/all",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() =>
      import("../components/companies/investors/requests").then((module) => ({
        default: module.InvestorRequests,
      }))
    ),
    path: "/company/:companyId/investors/requests",
    private: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() =>
      import("../components/companies/investors/settings").then((module) => ({
        default: module.InvestorsSettings,
      }))
    ),
    path: "/company/:companyId/investors/settings",
    private: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() =>
      import("../components/companies/investors/holdings/InvestorHoldings").then((module) => ({
        default: module.InvestorHoldings,
      }))
    ),
    path: "/company/:companyId/investors/holdings/:shareholderId",
    private: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() =>
      import("../components/companies/investors/add").then((module) => ({
        default: module.AddInvestor,
      }))
    ),
    path: "/company/:companyId/investors/add",
    private: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() =>
      import("../components/companies/investors/requests/ViewRequest").then((module) => ({
        default: module.ViewRequest,
      }))
    ),
    path: ["/company/:companyId/investors/view-request", "/company/:companyId/investors/review-request"],
    private: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/companyInformation")),
    path: "/company/:companyId/company-information",
    exact: true,
    private: true,

    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/companyInformation/optionsPlans/createOptionsPlan")),
    path: "/company/:companyId/company-information/options/grant",
    exact: true,
    private: true,

    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/register/shares")),
    path: "/company/:companyId/shares-register",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/register/options")),
    path: "/company/:companyId/options-register",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/register/notes")),
    path: "/company/:companyId/notes-register",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/register/expiringKyc")),
    path: "/company/:companyId/expiring-kyc-register",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/register/actOnBehalfRequests")),
    path: "/company/:companyId/pending-authority-requests",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/register/pendingAccruedUnits")),
    path: "/company/:companyId/pending-accrued-units",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/register/investorHoldingDetails")),
    path: "/company/:companyId/register/investor-holdings/:shareholderId",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() =>
      import("../components/companies/register/investorHoldingDetails/edit").then((module) => ({
        default: module.EditOwnerDetailsPage,
      }))
    ),
    path: [
      "/company/:companyId/register/investor-holdings/:shareholderId/edit",
      "/investor/company/:companyId/register/investor-holdings/:shareholderId/edit",
    ],
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/register/investorHoldingDetails/options")),
    path: [
      "/company/:companyId/register/investor-holdings/:shareholderId/options/:optionGrantId/details",
      "/investor/company/:companyId/investor-view/:shareholderId/options/:optionGrantId/details",
      "/investor/company/:companyId/investor-view/:shareholderId/switch-holding/options/:optionGrantId/details",
    ],
    private: true,
    exact: true,

    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/register/investorHoldingDetails/options/vest")),
    path: [
      "/company/:companyId/register/investor-holdings/:shareholderId/options/:optionGrantId/vest",
      "/investor/company/:companyId/investor-view/:shareholderId/options/:optionGrantId",
      "/investor/company/:companyId/investor-view/:shareholderId/switch-holding/options/:optionGrantId",
    ],
    private: true,
    exact: true,

    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/register/investorHoldingDetails/options/exercise")),
    path: ["/company/:companyId/register/investor-holdings/:shareholderId/options/:optionGrantId/exercise"],
    private: true,
    exact: true,

    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/register/investorHoldingDetails/notes")),
    path: [
      "/company/:companyId/register/investor-holdings/:shareholderId/notes/:notetransactionId",
      "/investor/company/:companyId/investor-view/:shareholderId/notes/:notetransactionId",
      "/investor/company/:companyId/investor-view/:shareholderId/switch-holding/notes/:notetransactionId",
    ],
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/companyInformation/optionsPools")),
    path: "/company/:companyId/company-information/options/pool-list",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/companyInformation/optionsPools/CreateOptionsPool")),
    path: "/company/:companyId/company-information/options/create-pools",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/companyInformation/officeholders")),
    path: "/company/:companyId/company-information/officeholders/list",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() =>
      import("../components/companies/companyInformation/fundraisingBankAccount").then((module) => ({
        default: module.Fundraise,
      }))
    ),
    path: "/company/:companyId/company-information/fundraise",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/companyInformation/notes")),
    path: "/company/:companyId/company-information/notes-summary",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/companyInformation/notes/create")),
    path: "/company/:companyId/company-information/create-notes",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/companyInformation/notes/issue")),
    path: "/company/:companyId/company-information/issue-notes",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/companyInformation/notes/certificate")),
    path: "/company/:companyId/company-information/notes-certificate",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/companyInformation/officeholders/appoint")),
    path: "/company/:companyId/company-information/officeholders/appoint",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/companyInformation/officeholders/edit")),
    path: "/company/:companyId/company-information/officeholders/:officeholderId/edit",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() =>
      import("../components/companies/companyInformation/RegisteredAddress").then((module) => ({
        default: module.EditRegisteredAddress,
      }))
    ),
    path: "/company/:companyId/company-information/registered-address",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() =>
      import("../components/companies/companyInformation/PrincipalPlaceOfBusiness").then((module) => ({
        default: module.EditPrincipalAddress,
      }))
    ),
    path: "/company/:companyId/company-information/principal-place-of-business",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/companyInformation/officeholders/remove")),
    path: "/company/:companyId/company-information/officeholders/remove",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/companyInformation/shares/sharePrice")),
    path: "/company/:companyId/company-information/share-price",
    private: true,
    exact: true,

    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/companyInformation/shares/tokenConfiguration")),
    path: "/company/:companyId/company-information/share-price/:shareClassId/token-configuration",
    private: true,
    exact: true,

    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/companyInformation/shares/shareCertificate")),
    path: "/company/:companyId/company-information/share-certificate",
    private: true,
    exact: true,

    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/companyInformation/shares/shareEligibility")),
    path: "/company/:companyId/company-information/share-eligibility",
    private: true,
    exact: true,

    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/companyInformation/shares/shareClaims")),
    path: "/company/:companyId/company-information/share-claims",
    private: true,
    exact: true,

    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/companyInformation/sharePriceHistory")),
    path: "/company/:companyId/company-information/share-price/history",
    private: true,
    exact: true,

    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/companyInformation/rateHistory")),
    path: "/company/:companyId/company-information/share-price/rate-history",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/register/shares/issueShares")),
    path: "/company/:companyId/shares-register/issue-share",
    private: true,
    exact: true,

    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/register/shares/transferShares")),
    path: "/company/:companyId/shares-register/transfer-shares",
    private: true,
    exact: true,

    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/settings")),
    path: "/company/:companyId/settings",
    private: true,
    exact: true,

    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/settings/ViewAdministrators")),
    path: "/company/:companyId/settings/administrators",
    private: true,
    exact: true,

    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/settings/TransferOwnership")),
    path: "/company/:companyId/settings/administrators/transfer-ownership",
    private: true,
    exact: true,

    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/settings/distributions")),
    path: "/company/:companyId/settings/distributions",
    private: true,
    exact: true,

    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/investor/redemptions/RequestRedemption")),
    path: "/investor/company/:companyId/investor-view/:shareholderId/redeem",
    private: true,
    exact: true,

    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/investor/redemptions/InvestorRedemptionDetail")),
    path: "/investor/company/:companyId/investor-view/:shareholderId/redeem/:redemptionId",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/investor/subscriptions/ApplyForUnits")),
    path: "/investor/company/:companyId/investor-view/:shareholderId/apply",
    private: true,
    exact: true,

    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/investor/subscriptions/InvestorSubscriptionDetail")),
    path: "/investor/company/:companyId/investor-view/:shareholderId/apply/:subscriptionId",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/settings/redemptions")),
    path: "/company/:companyId/settings/redemptions",
    private: true,
    exact: true,

    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/settings/yield")),
    path: "/company/:companyId/settings/yield",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/settings/vault")),
    path: "/company/:companyId/settings/vault",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/settings/apiKeyManagement")),
    path: "/company/:companyId/settings/api-key-management",
    private: true,
    exact: true,

    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/settings/bankAccounts")),
    path: "/company/:companyId/settings/bank-accounts",
    private: true,
    exact: true,

    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/settings/launchYourFund")),
    path: "/company/:companyId/settings/launch-your-fund",
    private: true,
    exact: true,

    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() =>
      import("../components/approvals/ApprovalsSettingsTab").then((m) => ({ default: m.ApprovalsSettingsTab }))
    ),
    path: "/company/:companyId/settings/approvals",
    private: true,
    exact: true,

    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("./Approvals").then((m) => ({ default: m.Approvals }))),
    path: "/company/:companyId/approvals",
    private: true,
    exact: true,

    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/documents")),
    path: "/company/:companyId/documents",
    private: true,
    exact: true,

    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/help")),
    path: "/help",
    private: true,
    exact: true,

    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/activity")),
    path: "/company/:companyId/activity",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/distributions")),
    path: "/company/:companyId/distributions",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/redemptions")),
    path: "/company/:companyId/redemptions",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/subscriptions")),
    path: "/company/:companyId/subscriptions",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/subscriptions/DealingWindow")),
    path: "/company/:companyId/subscriptions/window",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/subscriptions/DealingWindow")),
    path: "/company/:companyId/subscriptions/window/:windowKey",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/subscriptions/AllotmentRun")),
    path: "/company/:companyId/subscriptions/window/:windowKey/allotment-run",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/subscriptions/SubscriptionDetail")),
    path: "/company/:companyId/subscriptions/:subscriptionId",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/redemptions/DealingWindow")),
    path: "/company/:companyId/redemptions/window/:windowKey",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/redemptions/RedemptionDetail")),
    path: "/company/:companyId/redemptions/:redemptionId",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/redemptions/SettlementPayout")),
    path: "/company/:companyId/redemptions/:redemptionId/settlement",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() =>
      import("../components/companies/distributions/create").then((module) => ({
        default: module.CreateDistribution,
      }))
    ),
    path: "/company/:companyId/distributions/create",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() =>
      import("../components/companies/distributions/create").then((module) => ({
        default: module.CreateDistribution,
      }))
    ),
    path: "/company/:companyId/distributions/:distributionId/edit",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() =>
      import("../components/companies/distributions/review").then((module) => ({
        default: module.DistributionReview,
      }))
    ),
    path: "/company/:companyId/distributions/:distributionId/review",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() =>
      import("../components/companies/distributions/create").then((module) => ({
        default: module.DistributionDetail,
      }))
    ),
    path: "/company/:companyId/distributions/:distributionId",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() =>
      import("../components/companies/taxRules").then((module) => ({ default: module.TaxRulesPage }))
    ),
    path: "/company/:companyId/tax-rules",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/fundraise")),
    path: "/company/:companyId/fundraises",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() =>
      import("../components/companies/fundraise/create").then((module) => ({
        default: module.CreateFundraisingRound,
      }))
    ),
    path: "/company/:companyId/create-fundraise",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/fundraise/issue")),
    path: "/company/:companyId/fundraise-offer",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/companies/fundraise/fundraiseDetails")),
    path: "/company/:companyId/fundraises/:fundraiseId/details",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  // Investor Routes
  {
    component: lazy(() => import("../components/investor/Register")),
    exact: true,
    path: "/investor/create-account",

    container: lazy(() => import("../components/createAccount")),
  },
  {
    component: lazy(() => import("../components/companies/register/investor-view")),
    path: [
      "/investor/company/:companyId/investor-view/:shareholderId",
      "/investor/company/:companyId/investor-view/:shareholderId/switch-holding",
      "/",
      "/investor/company/:companyId/investor-view/:shareholderId/payment-details/:fundraiseOfferId",
    ],
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() =>
      import("../components/companies/register/investor-view/acceptAndPay").then((module) => ({
        default: module.AcceptAndPay,
      }))
    ),
    path: "/investor/company/:companyId/investor-view/:shareholderId/accept-and-pay/:fundraiseOfferId",
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() =>
      import("../components/companies/register/investor-view/switchHolding").then((module) => ({
        default: module.SwitchHolding,
      }))
    ),
    path: ["/investor/company/:companyId/switch-holding", "/investor/company/:companyId/investor-profiles/active"],
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() =>
      import("../components/investor/investorProfiles/requests").then((module) => ({
        default: module.InvestorRequests,
      }))
    ),
    path: ["/investor/company/:companyId/investor-profiles/requests"],
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() =>
      import("../components/investor/investorProfiles/existing").then((module) => ({
        default: module.ExistingProfiles,
      }))
    ),
    path: ["/investor/company/:companyId/investor-profiles/existing"],
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() =>
      import("../components/investor/investorProfiles/add").then((module) => ({
        default: module.AddInvestor,
      }))
    ),
    path: ["/investor/company/:companyId/investor-profiles/add"],
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  {
    component: lazy(() => import("../components/investor/Announcements")),
    path: [
      "/investor/company/:companyId/investor-relations/investor/:shareholderId/announcement",
      "/investor/company/:companyId/investor-relations/announcement",
    ],
    private: true,
    exact: true,
    container: lazy(() => import("../components/companies")),
    showSidebar: true,
  },
  // user routes
  {
    component: lazy(() =>
      import("../components/users/confirmEmailUpdate").then((module) => ({
        default: module.ConfirmEmailUpdate,
      }))
    ),
    path: "/user/confirm-email-update",
    exact: true,
    container: lazy(() => import("../components/pages")),
  },
  {
    component: lazy(() =>
      import("../components/users/cancelEmailUpdate").then((module) => ({
        default: module.CancelEmailUpdate,
      }))
    ),
    path: "/user/cancel-email-update",
    exact: true,
    container: lazy(() => import("../components/pages")),
  },
  {
    component: lazy(() => import("../components/investor/wallet").then((m) => ({ default: m.Wallet }))),
    path: ["/wallet", "/wallet/kyc-done"],
    private: true,
  },
];
